// File: client/src/variants/party/usePartyRuntime.js
//
// Party Chess runtime. Every 4 full turns the current player is offered 3 random
// modifiers to VOTE on (in vs-AI the computer auto-picks on its own turns). The
// chosen modifier applies immediately. Reuses the shared modifier catalog + Party's
// own upbeat modifiers. No engine changes.
//
// 'board' modifiers mutate the committed board; 'rule' modifiers set a per-turn flag
// (knight-only) read in transformLegalMoves; 'grant' (Bonus Turn) keeps the turn open.

import React, { useRef, useState, useCallback } from 'react';
import { PartyPopper } from 'lucide-react';
import { createPRNG } from '../../platform/prng';
import { offerChoices } from './partyModifiers';

export const CHOICE_EVERY = 4; // full turns between votes

const KNIGHT_OFFSETS = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]];

export function usePartyRuntime({ config, resumePayload }) {
  const isVsAI = config.opponent === 'ai';
  const humanColor = 'white';

  const seedRef = useRef(resumePayload?.seed ?? Math.floor(Math.random() * 1e9));
  const rngRef = useRef(createPRNG(seedRef.current));
  const moveCountRef = useRef(resumePayload?.moveCount || 0);
  const knightOnlyForRef = useRef(null);
  const grantBonusRef = useRef(false);      // Bonus Turn: keep the turn open once
  const chooserRef = useRef('white');       // whose choice a pending vote belongs to
  const lastBoardRef = useRef(null);
  const choicesRef = useRef(null);          // mirrors `choices` state for beforeMove gating

  const [choices, setChoicesState] = useState(null); // modifiers awaiting a pick
  const [message, setMessage] = useState(null);
  // Keep a ref in lock-step so beforeMove (which reads a ref) can gate on a pending vote.
  const setChoices = (v) => { choicesRef.current = v; setChoicesState(v); };

  // Apply a chosen modifier now (board mutation via lastBoardRef, or arm a rule/grant).
  const applyChoice = useCallback((mod) => {
    const board = lastBoardRef.current;
    const chooser = chooserRef.current;
    let msg = null;
    if (mod.kind === 'grant' && mod.grant === 'bonus_move') {
      grantBonusRef.current = true;
      msg = mod.apply();
    } else if (mod.kind === 'rule') {
      knightOnlyForRef.current = chooser;
      msg = mod.apply();
    } else if (board) {
      msg = mod.apply(board, rngRef.current, { chooser });
    }
    setChoices(null);
    setMessage(msg);
  }, []);

  const moveMiddleware = {
    beforeMove() {
      // While a human vote is pending, block moves until a modifier is chosen.
      if (choicesRef.current) {
        return { allow: false, reason: 'Choose a party modifier first! 🎉' };
      }
      return { allow: true };
    },

    transformLegalMoves(gameState, from, engineMoves) {
      lastBoardRef.current = gameState.board;
      if (knightOnlyForRef.current !== gameState.turn) return engineMoves;
      const board = gameState.board;
      const piece = board[from.row][from.col];
      if (!piece) return engineMoves;
      const isWhite = piece === piece.toUpperCase();
      const out = [];
      for (const [dr, dc] of KNIGHT_OFFSETS) {
        const r = from.row + dr, c = from.col + dc;
        if (r < 0 || r > 7 || c < 0 || c > 7) continue;
        const t = board[r][c];
        if (!t || t === '' || (t === t.toUpperCase()) !== isWhite) {
          out.push({ row: r, col: c, type: t ? 'capture' : 'normal' });
        }
      }
      return out;
    },

    afterMove(newState) {
      lastBoardRef.current = newState.board;
      moveCountRef.current += 1;

      // Spend a used knight-only flag.
      if (knightOnlyForRef.current === newState.turn) knightOnlyForRef.current = null;

      // Bonus Turn: keep the turn open once for the chooser.
      let keepOpen = false;
      if (grantBonusRef.current) {
        grantBonusRef.current = false;
        keepOpen = true;
      }

      // Offer a vote every CHOICE_EVERY full turns (to the side about to move).
      if (!keepOpen && moveCountRef.current % (CHOICE_EVERY * 2) === 0) {
        const nextColor = newState.turn === 'white' ? 'black' : 'white';
        chooserRef.current = nextColor;
        const opts = offerChoices(rngRef.current, 3);
        if (isVsAI && nextColor !== humanColor) {
          // AI auto-picks a random option immediately.
          const pick = opts[Math.floor(rngRef.current() * opts.length)];
          // Defer applying until the board ref reflects newState (it does now).
          lastBoardRef.current = newState.board;
          if (pick.kind === 'grant') { /* AI skips bonus-move grant for simplicity */ setMessage(`Computer chose ${pick.name}.`); }
          else if (pick.kind === 'rule') { knightOnlyForRef.current = nextColor; setMessage(`Computer chose ${pick.name}.`); }
          else { const m = pick.apply(newState.board, rngRef.current, { chooser: nextColor }); setMessage(`Computer chose ${pick.name}: ${m}`); }
        } else {
          setChoices(opts); // human votes via the overlay
        }
      }

      return { turnEnded: !keepOpen };
    },
  };

  const getPersistencePayload = useCallback(() => ({
    seed: seedRef.current,
    moveCount: moveCountRef.current,
  }), []);

  const renderOverlay = (slot) => {
    if (slot === 'top') {
      const untilNext = (CHOICE_EVERY * 2) - (moveCountRef.current % (CHOICE_EVERY * 2));
      return (
        <div className="flex flex-col items-center gap-1 my-2">
          <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-pink-600/40 border-pink-400/50 text-pink-200">
            <PartyPopper size={14} /> Party vote in {untilNext} move{untilNext === 1 ? '' : 's'}
          </span>
          {message && <span className="text-[11px] text-amber-300">🎉 {message}</span>}
        </div>
      );
    }
    if (slot === 'bottom' && choices) {
      return (
        <div className="w-full max-w-2xl mt-4 px-4">
          <p className="text-slate-200 text-sm font-medium mb-2 text-center">
            🎉 Choose a party modifier ({chooserRef.current}):
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {choices.map(mod => (
              <button
                key={mod.id}
                onClick={() => applyChoice(mod)}
                className="p-3 rounded-lg border-2 bg-gradient-to-br from-pink-600 to-fuchsia-700 border-pink-300 text-white text-center hover:opacity-90 transition"
                title={mod.describe}
              >
                <div className="text-xl">{mod.icon}</div>
                <div className="text-xs font-bold">{mod.name}</div>
                <div className="text-[10px] opacity-80">{mod.describe}</div>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return { moveMiddleware, getPersistencePayload, renderOverlay };
}
